import { confirmResetPassword, resetPassword, signIn } from '@aws-amplify/auth';
import { FieldErrors, Input } from '@chiroup/components';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { useForm } from '@chiroup/hooks';
import { EyeIcon, StarIcon } from '@heroicons/react/24/solid';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import conditions from '../../images/conditions.png';
import Button from '../common/Button';
import LoggedOutFooter from '../layout/LoggedOutFooter';
import Logo from '../layout/Logo';
import { ForgotPasswordStep } from './ForgotPassword';

const Migration = () => {
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { value, registerSubmit, onChange, errors, isSubmitting } = useForm(
    { email: '', code: '', password: '' },
    {
      email: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.sendCode && !val.email) {
              return 'Email is required.';
            }
            return false;
          },
        },
      },
      code: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.setPassword && !val.code) {
              return 'Enter the verification code you received in your email.';
            }
            return false;
          },
        },
      },
      password: {
        function: {
          value: (val) => {
            if (step === ForgotPasswordStep.setPassword && !val.password) {
              return 'Password is required.';
            }
            return false;
          },
        },
      },
    },
  );
  const [step, setStep] = useState<ForgotPasswordStep>(
    ForgotPasswordStep.sendCode,
  );
  const [error, setError] = useState<string>();

  const onSubmit = async (
    val: Partial<{ email: string; code: string; password: string }>,
  ) => {
    if (!val.email) {
      return val;
    }
    try {
      setError(undefined);
      if (step === ForgotPasswordStep.sendCode) {
        await resetPassword({ username: val.email });
        setStep(ForgotPasswordStep.setPassword);
      } else if (step === ForgotPasswordStep.setPassword) {
        if (!val.code || !val.password || !val.email) {
          return;
        }
        await confirmResetPassword({
          username: val.email,
          confirmationCode: val.code,
          newPassword: val.password,
        });
        await signIn({ username: val.email, password: value.password });
        navigate('/');
        return value;
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message?.replace('UserMigration failed with error ', ''));
    }
    return val;
  };

  return (
    <div className="bg-white dark:bg-darkGray-900">
      <main>
        <div className="pt-8 overflow-hidden sm:pt-12 lg:relative lg:py-48">
          <div className="mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl lg:grid lg:grid-cols-2 lg:gap-24">
            <div>
              <Logo className="h-16" />
              <div className="mt-20">
                <div>
                  <span className="rounded bg-primary-50 dark:bg-primary-800 px-2.5 py-1 text-xs font-semibold text-primary-500 tracking-wide uppercase">
                    Setup your account
                  </span>
                </div>
                <div className="mt-6 sm:max-w-xl">
                  <h1 className="text-4xl font-extrabold text-gray-900 dark:text-darkGray-50 tracking-tight leading-tight sm:text-5xl">
                    Next level evidence-based chiropractic
                  </h1>
                  <p className="mt-6 text-xl text-gray-500 dark:text-darkGray-400">
                    {step === ForgotPasswordStep.sendCode
                      ? `Not just a fresh look. The new ChiroUp has great new
                    features and was built to be the starting point for the next
                    generation of Chiropractic tools. Enter your email below to
                    verify your address and set your password for the new
                    platform.`
                      : `We sent a verification code to your email. Please enter it in here along with your new password.`}
                  </p>
                </div>

                <form
                  className="space-y-6"
                  onSubmit={registerSubmit(onSubmit, {})}
                  autoComplete="off"
                >
                  {step === ForgotPasswordStep.sendCode ? (
                    <div className="grid grid-cols-1 gap-4">
                      <Input
                        label="Email *"
                        value={value.email}
                        onChange={onChange('email')}
                        autoComplete="email"
                        name="email"
                        className="w-full"
                        errors={errors.fieldErrors?.email}
                        autoFocus
                      />
                      <div className="flex">
                        <Button
                          type="submit"
                          className="block w-full rounded-md border border-transparent px-5 py-3 bg-primary-500 text-base font-medium text-white shadow hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:px-10"
                          text="Send code"
                          loading={isSubmitting}
                          fullHeight
                        />
                      </div>
                      {
                        !!errors?.form?.map((err: FormError, i: number) => (
                          <FieldErrors errors={err} key={i} />
                        ))
                      }
                      {!!error && <FieldErrors errors={{ message: error }} />}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1">
                      <div className="sm:max-w-lg sm:w-full sm:flex">
                        <div className="grid grid-cols-1 w-full gap-4">
                          <Input
                            label="Verification code *"
                            value={value.code}
                            className="w-full"
                            onChange={onChange('code')}
                            name="code"
                            errors={errors.fieldErrors?.code}
                            autoComplete="one-time-code"
                            autoFocus
                          />
                          <Input
                            label="Set password *"
                            value={value.password}
                            onChange={onChange('password')}
                            name="password"
                            className="w-full"
                            errors={errors.fieldErrors?.password}
                            autoComplete="new-password"
                            type="password"
                            ref={passwordRef}
                            icon={
                              <EyeIcon
                                className="text-gray-300 hover:text-gray-400 h-5 w-5 cursor-pointer"
                                onClick={() => {
                                  if (passwordRef.current?.type) {
                                    passwordRef.current.type =
                                      passwordRef.current.type === 'password'
                                        ? 'text'
                                        : 'password';
                                  }
                                }}
                              />
                            }
                            clickIcon
                          />
                          <div className="flex">
                            <Button
                              type="submit"
                              className="block w-full rounded-md border border-transparent px-5 py-3 bg-primary-500 text-base font-medium text-white shadow hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:px-10"
                              text="Set password"
                              loading={isSubmitting}
                              fullHeight
                            />
                          </div>
                        </div>
                      </div>
                      {
                        !!errors?.form?.map((err: FormError, i: number) => (
                          <FieldErrors errors={err} key={i} />
                        ))
                      }
                      {!!error && <FieldErrors errors={{ message: error }} />}
                    </div>
                  )}
                </form>
                <div className="mt-6">
                  <div className="inline-flex items-center divide-x divide-gray-300">
                    <div className="flex-shrink-0 flex pr-5">
                      <StarIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                      <StarIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                      <StarIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                      <StarIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                      <StarIcon
                        className="h-5 w-5 text-yellow-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="min-w-0 flex-1 pl-5 py-1 text-sm text-gray-500 dark:text-darkGray-400 sm:py-3">
                      <span className="font-medium text-gray-900 dark:text-darkGray-50">
                        Rated 5 stars
                      </span>{' '}
                      on Google{' '}
                      <span className="font-medium text-primary-500">
                        by over 110 users.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-12">
                  <LoggedOutFooter />
                </div>
              </div>
            </div>
          </div>

          <div className="sm:mx-auto sm:max-w-3xl sm:px-6">
            <div className="py-12 sm:relative sm:mt-12 sm:py-16 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
              <div className="hidden sm:block">
                <div className="absolute inset-y-0 left-1/2 w-screen bg-gray-50 dark:bg-darkGray-700 rounded-l-3xl lg:left-80 lg:right-0 lg:w-full" />
                <svg
                  className="absolute top-8 right-1/2 -mr-3 lg:m-0 lg:left-0"
                  width={404}
                  height={392}
                  fill="none"
                  viewBox="0 0 404 392"
                >
                  <defs>
                    <pattern
                      id="837c3e70-6c3a-44e6-8854-cc48c737b659"
                      x={0}
                      y={0}
                      width={20}
                      height={20}
                      patternUnits="userSpaceOnUse"
                    >
                      <rect
                        x={0}
                        y={0}
                        width={4}
                        height={4}
                        className="text-gray-300"
                        fill="currentColor"
                      />
                    </pattern>
                  </defs>
                  <rect
                    width={404}
                    height={392}
                    fill="url(#837c3e70-6c3a-44e6-8854-cc48c737b659)"
                  />
                </svg>
              </div>
              <div className="relative pl-4 -mr-40 sm:mx-auto sm:max-w-3xl sm:px-0 lg:max-w-none lg:h-full lg:pl-12">
                <img
                  className="w-full rounded-md shadow-xl ring-1 ring-black ring-opacity-5 lg:h-full lg:w-auto lg:max-w-none"
                  src={conditions}
                  alt="ChiroUp"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Migration;
